import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  transparentOnTop?: boolean;
}

const navItems = [
  { label: '首页', to: '/' },
  { label: '事件合集', to: '/timeline' },
  { label: '关于我们', to: '/about' },
  { label: '联系我们', to: '/contact' },
];

export default function Navbar({ transparentOnTop = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(!transparentOnTop);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!transparentOnTop) {
      setScrolled(true);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [transparentOnTop]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = scrolled;
  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? 'border-b border-background-200 bg-background-50/95 backdrop-blur'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center justify-between px-4 md:h-20 md:px-6">
        <Link to="/" className="flex items-center gap-2.5" aria-label="蝌蚪吃瓜 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-lg font-bold text-background-50">
            蝌
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg font-bold tracking-tight ${
                solid ? 'text-foreground-950' : 'text-background-50'
              }`}
            >
              蝌蚪吃瓜
            </span>
            <span
              className={`mt-0.5 font-label text-[10px] tracking-[0.2em] ${
                solid ? 'text-foreground-500' : 'text-background-100/80'
              }`}
            >
              TIMELINE
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`rounded-md px-4 py-2 font-label text-sm transition-colors ${
                isActive(item.to)
                  ? solid
                    ? 'bg-background-100 text-primary-600'
                    : 'bg-background-50/15 text-background-50'
                  : solid
                    ? 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                    : 'text-background-100/90 hover:bg-background-50/15 hover:text-background-50'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/timeline"
            className="hidden items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 md:flex"
          >
            <i className="ri-search-line" />
            搜索事件
          </Link>
          <button
            type="button"
            aria-label={open ? '关闭菜单' : '打开菜单'}
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
            className={`flex h-10 w-10 items-center justify-center rounded-md md:hidden ${
              solid ? 'text-foreground-950' : 'text-background-50'
            }`}
          >
            <i className={`${open ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-4 py-3 font-label text-sm transition-colors ${
                  isActive(item.to)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-800 hover:bg-background-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/timeline"
              className="mt-2 flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-3 font-label text-sm text-background-50"
            >
              <i className="ri-search-line" />
              搜索事件
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}